import axios from 'axios';
import type {
  CreateInventoryReceivedItemPayload,
  CreateInventoryReceivedItemResponse,
  ReceivingWriteStep,
  RollbackInventoryReceivedItemResult,
  ShipmentsListResponse,
  ShipmentDetailResponse,
  ShipmentItemDetailResponse,
  ShopifySyncResult,
} from '../types/airtableTypes';
import { RECEIVING_WRITE_STEPS } from '../types/airtableTypes';

/** Mirrors backend `RECEIVING_DATA_SOURCE` from `GET /api/auth/status`. */
export type ServerReceivingDataSource = 'airtable' | 'dual' | 'supabase';

type LocalStorageReceivingOverride = 'airtable' | 'supabase';

const STORAGE_KEY = 'rsv_receiving_data_source';

let serverReceivingDataSource: ServerReceivingDataSource = 'supabase';

/**
 * Called from `AuthContext` after each `GET /api/auth/status` (before `loading` clears).
 * Defaults to `supabase` when the field is missing (older mocks).
 */
export function configureReceivingFromServer(
  payload: { receivingDataSource?: ServerReceivingDataSource } | undefined
): void {
  serverReceivingDataSource = payload?.receivingDataSource ?? 'supabase';
}

function localStorageReceivingOverride(): LocalStorageReceivingOverride | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'airtable') return 'airtable';
    if (stored === 'supabase') return 'supabase';
  } catch {
    /* SSR / private mode */
  }
  return null;
}

/** True when list/detail/item GETs should use `/api/supabase/receiving`. */
function readUsesSupabase(): boolean {
  const lo = localStorageReceivingOverride();
  if (lo === 'airtable') return false;
  if (lo === 'supabase') return true;
  return serverReceivingDataSource === 'dual' || serverReceivingDataSource === 'supabase';
}

function readBasePath(): string {
  return readUsesSupabase() ? '/api/supabase/receiving' : '/api/airtable';
}

function writeBasePath(): string {
  return serverReceivingDataSource === 'supabase' ? '/api/supabase/receiving' : '/api/airtable';
}

/** Cache key segment: `at` = Airtable reads, `sb` = Supabase reads. */
export function getShipmentCacheReadNamespace(): 'at' | 'sb' {
  return readUsesSupabase() ? 'sb' : 'at';
}

export interface ShipmentsListResponseWithError extends ShipmentsListResponse {
  error?: string;
}

export const fetchUnreceivedShipments = async (): Promise<ShipmentsListResponseWithError> => {
  try {
    const response = await axios.get(`${readBasePath()}/shipments`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching shipments:', error);
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.error || error.message;
      console.error(`Shipments API error: status=${status}, message=${message}`);
      if (status === 401) {
        return { shipments: [], error: 'Authentication required. Please log in.' };
      }
      return { shipments: [], error: `Failed to load shipments: ${message}` };
    }
    return { shipments: [], error: 'Failed to load shipments' };
  }
};

export const fetchShipment = async (shipmentId: string): Promise<ShipmentDetailResponse | null> => {
  try {
    const path =
      shipmentId === 'all'
        ? `${readBasePath()}/shipments/all`
        : `${readBasePath()}/shipments/${shipmentId}`;
    const response = await axios.get(path);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching shipment:', error);
    return null;
  }
};

export const fetchShipmentItem = async (shipmentItemId: string): Promise<ShipmentItemDetailResponse | null> => {
  try {
    const response = await axios.get(`${readBasePath()}/shipment-items/${shipmentItemId}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching shipment item:', error);
    return null;
  }
};

export const createInventoryReceivedItem = async (
  payload: CreateInventoryReceivedItemPayload
): Promise<CreateInventoryReceivedItemResponse | null> => {
  try {
    const response = await axios.post(`${writeBasePath()}/inventory-received-items`, payload);
    return response.data;
  } catch (error: unknown) {
    console.error('Error creating inventory received item:', error);
    throw error;
  }
};

export const rollbackInventoryReceivedItem = async (
  receivedItemId: string,
  shipmentItemId: string
): Promise<RollbackInventoryReceivedItemResult> => {
  try {
    const response = await axios.post(
      `${writeBasePath()}/inventory-received-items/${encodeURIComponent(receivedItemId)}/rollback`,
      { shipmentItemId }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error rolling back inventory received item:', error);
    throw error;
  }
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function receivingWriteStepFromUnknown(value: unknown): ReceivingWriteStep | null {
  if (value === RECEIVING_WRITE_STEPS.lookup) return RECEIVING_WRITE_STEPS.lookup;
  if (value === RECEIVING_WRITE_STEPS.supabase) return RECEIVING_WRITE_STEPS.supabase;
  if (value === RECEIVING_WRITE_STEPS.shopify) return RECEIVING_WRITE_STEPS.shopify;
  return null;
}

const GENERIC_PUBLIC_ERROR = 'An internal error occurred';

function serverErrorDetail(errorText: string | null): string {
  if (!errorText || errorText === GENERIC_PUBLIC_ERROR) return '';
  return ` ${errorText}`;
}

/** User-visible text for a failed Record Receipt POST (lookup / Supabase / network). */
export function formatReceivingSubmitError(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return 'Failed to record received inventory.';
  }

  if (!error.response) {
    return 'Failed to record received inventory (network or timeout). Check the received list before retrying — the receipt may already be saved.';
  }

  const data = error.response.data;
  const step = isRecord(data) ? receivingWriteStepFromUnknown(data.step) : null;
  const serverError = isRecord(data) && typeof data.error === 'string' ? data.error : null;
  const detail = serverErrorDetail(serverError);

  if (step === RECEIVING_WRITE_STEPS.supabase) {
    return `Failed to save receipt to warehouse database. Shopify was not updated.${detail}`;
  }
  if (step === RECEIVING_WRITE_STEPS.lookup) {
    return `Failed to look up this item for receiving. Warehouse receipt was not saved. Shopify was not updated.${detail}`;
  }
  if (step === RECEIVING_WRITE_STEPS.shopify) {
    return `Warehouse receipt saved. Shopify inventory sync failed.${detail}`;
  }
  return serverError || 'Failed to record received inventory.';
}

/** User-visible text when the receipt saved but Shopify sync did not. */
export function formatReceivingShopifyWarning(quantity: number, shopifySync: ShopifySyncResult): string {
  const detail = shopifySync.error ? `: ${shopifySync.error}` : '.';
  return `Successfully recorded ${quantity} units. Shopify inventory sync failed${detail}`;
}

/** User-visible text for a failed rollback POST. */
export function formatReceivingRollbackError(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return 'Failed to roll back receipt.';
  }

  if (!error.response) {
    return 'Failed to roll back receipt (network or timeout). Check the received list before retrying.';
  }

  const data = error.response.data;
  const step = isRecord(data) ? receivingWriteStepFromUnknown(data.step) : null;
  const serverError = isRecord(data) && typeof data.error === 'string' ? data.error : null;
  const detail = serverErrorDetail(serverError);

  if (step === RECEIVING_WRITE_STEPS.shopify) {
    return `Failed to reverse Shopify inventory. The receipt was not rolled back.${detail}`;
  }
  if (step === RECEIVING_WRITE_STEPS.supabase) {
    return `Failed to save rollback to warehouse database.${detail}`;
  }
  if (step === RECEIVING_WRITE_STEPS.lookup) {
    return `Failed to look up this receipt for rollback.${detail}`;
  }
  if (error.response.status === 409) {
    return serverError || 'This receipt is already rolled back.';
  }
  if (error.response.status === 404) {
    return serverError || 'Receipt not found.';
  }
  return serverError || 'Failed to roll back receipt.';
}
